import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { useConfig, useLoading, useNotification, useFilter, useSort, useVuexPageLimit, useDateFormat } from "./";
import { State } from "@/store/ota";
import { getOtas } from "@/services/ota";
import { JCLoadFileOrm } from "../../../common/src/models/postgres/jcLoadFile";

export default function useOTA(packageAID: string) {
  const store = useStore();
  const state = computed<State>(() => store.state.ota);
  const { t } = useI18n();
  const { SORT_NONE } = useConfig();
  const { setLoading } = useLoading();
  const { showError } = useNotification();
  const { timezoneOffset } = useDateFormat();
  const moduleName = `ota-${packageAID}`;

  const sortProps = computed(() => {
    return {
      id: SORT_NONE,
      label: SORT_NONE,
      version: SORT_NONE,
      builtAt: SORT_NONE,
      createdAt: SORT_NONE,
      updatedAt: SORT_NONE,
    };
  });
  const items = computed(() => state.value.items[packageAID]);
  const setItems = (items: JCLoadFileOrm[]) => store.commit(`ota/SET_ITEMS`, { items, packageAID });
  if (!items.value) {
    setItems([]);
  }
  const { totalPage, totalItem, take, takes, skip, currentPage, setTotalItem, setTotalPage, setLimit, setCurrentPage, resetPaginate } =
    useVuexPageLimit(moduleName);
  const { sortAttrs, sortAttrsValue, setSortColumnValue } = useSort(moduleName, sortProps.value, async () => {
    fetchPackages();
  });
  const { filterAttrs, moduleAttrs, setFilterQuery, setFilter } = useFilter(moduleName);
  const _setFilterQuery = async (attrs: any, packageAID: string, enableHistory = true) => {
    await setFilterQuery(attrs, { name: "ota_type", params: { packageAID } }, enableHistory);
  };

  const fetchPackages = async () => {
    try {
      setLoading(true);
      setItems([]);
      const response = await getOtas({
        packageAID,
        take: take.value,
        skip: skip.value,
        pageIndex: currentPage.value,
        ...sortAttrsValue.value,
        ...filterAttrs.value,
        timezoneOffset,
      });
      const { data, pageIndex, totalPages, totalItems } = response;
      setItems(data);
      setTotalItem(totalItems);
      setTotalPage(totalPages);
      setCurrentPage(pageIndex);
    } catch (e: any) {
      showError(e.response?.data?.message || e.message || t("ProcessError", { process: t("ota.FetchList") }));
    } finally {
      setLoading(false);
    }
  };

  return {
    // pagination
    items,
    totalPage,
    totalItem,
    take,
    takes,
    skip,
    currentPage,
    setTotalItem,
    setTotalPage,
    setItems,
    setLimit,
    setCurrentPage,
    resetPaginate,
    // sort
    sortAttrs,
    sortAttrsValue,
    setSortColumnValue,
    // filter
    filterAttrs,
    moduleAttrs,
    setFilterQuery: _setFilterQuery,
    setStoreFilter: setFilter,

    fetchPackages,
  };
}
